import type { BloodPressureWithRelations } from "@ch/generated";
import type { IReturnBmiChartData } from "@ch/helpers/hooks/useBmiGraphDataGet.ts";
import {
  bPBodyPositionOptions,
  sourceOptionsPrescriptions,
} from "@ch/helpers/utils/globalConstants.ts";
import type { IReturnBGChartData } from "@ch/pages/BloodGlucosePage/hooks/useBloodGlucoseGraphDataGet.ts";
import type { IReturnBloodOxygenChartData } from "@ch/pages/BloodOxygenPage/hooks/useBloodOxygenGraphDataGet.ts";
import type { ContextDatasetType } from "@ch/pages/BloodPressurePage/BloodPressurePage.tsx";
import type { IReturnBPChartData } from "@ch/pages/BloodPressurePage/hooks/useBloodPressureGraphDataGet.ts";
import type { IReturnCholesterolChartData } from "@ch/pages/CholesterolPage/hooks/useCholesterolGraphDataGet.ts";
import type { IReturnExerciseChartData } from "@ch/pages/ExercisePage/hooks/useExerciseGraphDataGet.ts";
import type { IReturnHba1cChartData } from "@ch/pages/Hba1cInputPage/hooks/useHba1cGraphDataGet.ts";
import type { IReturnHeartRateChartData } from "@ch/pages/HeartRatePage/hooks/useHeartRateGraphDataGet.ts";
import type { IReturnHeightChartData } from "@ch/pages/Height/hooks/useHeightGraphDataGet.ts";
import type { IReturnHRVChartData } from "@ch/pages/HRVPage/hooks/useHRVGraphDataGet.ts";
import type { IReturnPSAChartData } from "@ch/pages/PSAPage/hooks/usePSAGraphDataGet.ts";
import type { IReturnRespirationChartData } from "@ch/pages/RespirationPage/hooks/useRespirationGraphDataGet.ts";
import type { IReturnStepsChartData } from "@ch/pages/StepsPage/hooks/useStepsGraphDataGet.ts";
import type { IReturnStressChartData } from "@ch/pages/StressPage/hooks/useStressGraphDataGet.ts";
import type { IReturnTemperatureChartData } from "@ch/pages/TemperaturePage/hooks/useTemperatureGraphDataGet.ts";
import type { IReturnWaistChartData } from "@ch/pages/WaistCircumferencePage/hooks/useWaistGraphDataGet.ts";
import type { IReturnWeightChartData } from "@ch/pages/WeightPage/hooks/useWeightGraphDataGet.ts";
import type { TooltipItem } from "chart.js";
import parse from "html-react-parser";
import moment from "moment";
import type { JSXElementConstructor, ReactElement } from "react";
import { type ExternalToast, toast } from "sonner";
import type { Breakpoints, TObject, TObjectValues } from "./types";

// returns a new array sorted by `order`; falls back to `duration` when `order` is missing
export const sortByOrder = <
  T extends { order?: number | null; duration?: number | null },
>(
  list?: T[] | null,
): T[] =>
  [...(list || [])].sort(
    (a, b) => (a.order ?? a.duration ?? 0) - (b.order ?? b.duration ?? 0),
  );

export const findIndexInArray = <T>(
  originalArray: T[],
  objectToFInd: T,
  key: keyof T,
) => {
  return originalArray.findIndex((item) => {
    return item[key] === objectToFInd[key];
  });
};

export const returnUpdatedList = <T>(
  newObj: T,
  oldList: T[],
  key: keyof T = "id" as keyof T,
) => {
  const indexOfLocal = findIndexInArray(oldList, newObj, key);
  const list = [...oldList];
  if (indexOfLocal !== -1) list[indexOfLocal] = newObj;
  return list;
};

export const numberToLetter = (index: number) => {
  if (typeof index !== "number" || index < 0 || !Number.isInteger(index)) {
    return "Invalid input";
  }
  const letter = String.fromCharCode(65 + index);

  return letter;
};

export const removeDuplicates = (
  myArr: Record<string, unknown>[],
  prop: string,
) => {
  return myArr.filter((obj, pos, arr) => {
    return arr.map((mapObj) => mapObj[prop]).indexOf(obj[prop]) === pos;
  });
};

// rounds up a number(value) to x decimal places
// it returns a whole number when value is whole
export const roundUp = (value: number, x: number) => {
  const multiplier = 10 ** (x ?? 0);
  return Math.round(value * multiplier) / multiplier;
};

// function to add st, nd, rd, th to positions
export const getNumberPosition = (number: number) => {
  const lastDigit = number % 10;
  const k = number % 100;

  if (lastDigit === 1 && k !== 11) {
    return `${number}st`;
  }
  if (lastDigit === 2 && k !== 12) {
    return `${number}nd`;
  }
  if (lastDigit === 3 && k !== 13) {
    return `${number}rd`;
  }
  return `${number}th`;
};

export const getUrlParam = (param: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

// Usage
// const help_button = await waitForDomElement('#help-btn');
export function waitForDomElement(selector: string) {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      resolve(document.querySelector(selector));
    }
    const observer = new MutationObserver(() => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  });
}

export const isEmpty = (value: string | null) => {
  return (
    value == null || (typeof value === "string" && value.trim().length === 0)
  );
};

export const calculateDatePercentage = (
  startDate: Date,
  endDate: Date,
): number => {
  const now = new Date().getTime();
  const start = startDate.getTime();
  const end = endDate.getTime();
  const range = end - start;
  const val = Math.ceil((100 * (now - start)) / range);
  if (val > 100) {
    return 100;
  }
  if (val < 0) {
    return 0;
  }
  return val;
};

export const breakpoints: Breakpoints = {
  base: 480,
  md: 768,
  lg: 992,
  xl: 1280,
  xxl: 1536,
};

export const parseHtml = parse as (html: string) => string;

export const scrollToSection = (id: string) => {
  const section = document.getElementById(id);
  if (!section) {
    return;
  }
  section.scrollIntoView({ behavior: "smooth" });
};

export async function getUserLocation() {
  const position = await new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject);
  });

  return position;
}

export const passwordPattern =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{};:,<.>]).{8,}$/;

export const isObjectEmpty = (obj: Record<string, unknown>) => {
  return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
};

export function objectToFormData<T extends Record<string, string | Blob>>(
  obj: T,
): FormData {
  const formData = new FormData();

  Object.entries(obj).forEach(([key, value]) => {
    formData.append(key, value);
  });
  return formData;
}

export function removeObjectsWithEmptyStrings(arr: Record<string, unknown>[]) {
  // Filter out objects with empty strings
  const objectKeys = Object.keys(arr);

  return arr.filter((obj) => {
    return objectKeys.some((key) => obj[key] !== "");
  });
}

export const getFilenameFromUrl = (url?: string) => {
  if (url) {
    const urlObject = new URL(url);
    const pathWithFilename = urlObject.pathname;
    const filename = pathWithFilename.substring(
      pathWithFilename.lastIndexOf("/") + 1,
    );
    return filename;
  }
  return url;
};

export const NotifyError = (message: string, data: ExternalToast = {}) => {
  const { style, ...rest } = data;
  toast.error(message, { style: { ...style }, duration: 6000, ...rest });
};

export const NotifySuccess = (message: string, data: ExternalToast = {}) => {
  const { style, ...rest } = data;
  toast.success(message, {
    style: { ...style },
    position: "top-right",
    duration: 3000,
    ...rest,
  });
};

export const NotifyInfo = (message: string, data: ExternalToast = {}) => {
  const { style, ...rest } = data;
  toast.info(message, { style: { ...style }, duration: 4500, ...rest });
};

export const NotifyCustom = (
  message: ReactElement<unknown, string | JSXElementConstructor<unknown>>,
  data: ExternalToast = {},
) => {
  const { style, ...rest } = data;
  toast(message, { style: { ...style }, duration: 4500, ...rest });
};

export const roundToDecimalPlace = (num: number, decimalPlaces = 1) => {
  const p = 10 ** decimalPlaces;
  const n = num * p * (1 + Number.EPSILON);
  return Math.round(n) / p;
};

export const openLogoutModal = () => {
  const dialogue = document.getElementById(
    "logout-confirm-modal",
  ) as HTMLDialogElement | null;

  if (!dialogue) {
    return;
  }

  dialogue.showModal();
};

export const closeLogoutModal = () => {
  const dialogue = document.getElementById(
    "logout-confirm-modal",
  ) as HTMLDialogElement | null;

  if (!dialogue) {
    return;
  }

  dialogue.close();
};

export const openHealthDiaryLogErrorModal = (errorMessage: string) => {
  const dialogue = document.getElementById(
    "health-diary-log-error-modal",
  ) as HTMLDialogElement | null;

  if (!dialogue) {
    return;
  }
  const body = dialogue.querySelector(".modal-body");

  if (!body) {
    return;
  }

  body.innerHTML = `<p style="color: #FD6E5E">${errorMessage}</p>`;
  dialogue.showModal();
};

export const setPageDescription = (description: string) => {
  const metaDescription = document.querySelector('meta[name="description"]');
  if (metaDescription) {
    metaDescription.setAttribute("content", description);
  }
};

export const formatCurrency = (num?: number) => {
  if (!num) return num;

  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
export const dateIsWithinRange = (
  date: moment.Moment,
  minDate?: moment.Moment,
  maxDate?: moment.Moment,
) => {
  if (!minDate && !maxDate) {
    return true;
  }

  const minDateBool = minDate ? date.isSameOrAfter(minDate, "day") : true;

  const maxDateBool = maxDate ? date.isSameOrBefore(maxDate, "day") : true;

  return minDateBool && maxDateBool;
};

export const filterDataByDateRange = <T>(
  data?: T[],
  dateKey = "created_at",
  startDate?: moment.Moment,
  endDate?: moment.Moment,
) => {
  if (!data || data?.length <= 0) {
    return [];
  }
  return data.filter((item) => {
    const date = moment((item as Record<string, string>)[dateKey]);
    return dateIsWithinRange(date, startDate, endDate);
  });
};

export const isPhoneNumberValid = (phone?: string, countryCode?: string) => {
  if (!phone || !countryCode) {
    return false;
  }

  const phoneHasCountryCode = phone.startsWith(countryCode);

  // Check if phone number alone is empty
  const isPhoneEmpty = phone.replace(countryCode, "").trim().length === 0;

  // Check if phone number does not have the selected country code
  if (!phoneHasCountryCode || isPhoneEmpty) {
    return false;
  }

  // If country code is +234, validate phone number with min, max length
  if (countryCode === "+234") {
    const phoneNumber = phone.replace(countryCode, "").trim();

    if (phoneNumber.length !== 10) {
      return false;
    }
  }

  return true;
};

export const linkify = (inputText: string) => {
  const replacePattern =
    /(^|[^"])(\b(https?:\/\/|www\.)[^<\s]+[^.,;'">:\s[\]{}])/gi;

  const replacedText = inputText.replace(
    replacePattern,
    (_match, prefix: string, url: string) => {
      const href = url.startsWith("www.") ? `http://${url}` : url;
      const linkText = url.length > 50 ? `${url.substring(0, 47)}...` : url; // Truncate long URLs for display
      return `${prefix}<a href="${href}" target="_blank">${linkText}</a>`;
    },
  );

  return replacedText;
};

export const getNumberValue = (value: TObjectValues | TObject) => {
  if (typeof value !== "number" && typeof value !== "string") {
    return 0;
  }

  return Number(value);
};

// // to be used with onKeyUp/Down
export const onlyNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const num = /^(0|[1-9]\d*)(\.\d+)?(e-?(0|[1-9]\d*))?$/gim;
  e.key = e.key.replace(num, " ");
};

// to be used with onKeyUp/Down
export const decimalNumbers = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const num = /^\d+(\.\d)?\d*$/;
  e.key = e.key.replace(num, " ");
};

export const formatChatTime = (timestamp: string) => {
  const now = moment();
  const messageTime = moment(timestamp);

  if (now.diff(messageTime, "days") < 1) {
    return messageTime.format("LT"); // Show time only
  }
  if (now.diff(messageTime, "days") < 7) {
    return messageTime.format("ddd LT"); // Show day of week and time
  }

  if (now.year() === messageTime.year()) {
    return messageTime.format("MMM Do"); // Show month, day
  }

  return messageTime.format("MMM D, YYYY"); // Show date, month, year
};

export const generateRandomCode = () => {
  const digits = "123456789";
  let reference = "";

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < 6; i++) {
    const randomIndex = Math.floor(Math.random() * digits.length);
    reference += digits[randomIndex];
  }
  return reference;
};

export const stripHtmlTags = (input: string): string => {
  const htmlTagPattern = /<\/?[^>]+(>|$)/g;
  return input.replace(htmlTagPattern, "").replace(/&nbsp;/g, " ");
};

export const slugify = (str: string) => {
  return String(str)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
};

export const getSourceName = (source: string) => {
  return sourceOptionsPrescriptions?.find((option) => option?.value === source)!
    .label;
};

export const tooltipBpPlugins = {
  plugins: {
    tooltip: {
      // Adds dynamic messages to the tooltip
      callbacks: {
        label(context: TooltipItem<"line">) {
          const value = context.parsed.y;
          const customMessages = (
            context.dataset as ContextDatasetType<typeof context.dataset>
          ).tooltipMessages;
          const customText = customMessages
            ? customMessages[context.dataIndex]
            : null;

          return customText ?? `Value: ${value}`;
        },
      },
    },
  },
};

export const getTooltipBPMessages = (
  filteredData: BloodPressureWithRelations[],
  type: "systolic" | "diastolic",
) =>
  filteredData.map((d) => {
    const bodyPositioningText = bPBodyPositionOptions.find(
      (o) => o.value === d.bodyPositioning,
    )?.label;
    return d.bodyPositioning ? `${d[type]} ${bodyPositioningText}` : "";
  });

export const getTooltipBPMessagesFromAverage = <T extends IReturnBPChartData>(
  filteredData: T[],
  type: "systolic" | "diastolic",
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d[type].max}`,
      `Avg: ${d[type].avg}`,
      `Min: ${d[type].min}`,
    ];
  });

export const getTooltipBGMessagesFromAverage = <T extends IReturnBGChartData>(
  filteredData: T[],
  type: "beforeMeal" | "afterMeal",
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d[type].max}`,
      `Avg: ${d[type].avg}`,
      `Min: ${d[type].min}`,
    ];
  });

export const getTooltipPSAMessagesFromAverage = <T extends IReturnPSAChartData>(
  filteredData: T[],
  type: "totalPsa" | "freePsa",
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d[type].max}`,
      `Avg: ${d[type].avg}`,
      `Min: ${d[type].min}`,
    ];
  });

export const getTooltipCholesterolMessagesFromAverage = <
  T extends IReturnCholesterolChartData,
>(
  filteredData: T[],
  type: "cholesterol" | "triglycerides" | "hdl" | "ldl",
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d[type].max}`,
      `Avg: ${d[type].avg}`,
      `Min: ${d[type].min}`,
    ];
  });

export const getTooltipWeightMessagesFromAverage = <
  T extends IReturnWeightChartData,
>(
  filteredData: T[],
  type: "value" | "goal",
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d[type].max}`,
      `Avg: ${d[type].avg}`,
      `Min: ${d[type].min}`,
    ];
  });

export const getTooltipTemperatureMessagesFromAverage = <
  T extends IReturnTemperatureChartData,
>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d.value.max}`,
      `Avg: ${d.value.avg}`,
      `Min: ${d.value.min}`,
    ];
  });

export const getTooltipHba1cMessagesFromAverage = <
  T extends IReturnHba1cChartData,
>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d.value.max}`,
      `Avg: ${d.value.avg}`,
      `Min: ${d.value.min}`,
    ];
  });

export const getTooltipExerciseMessagesFromAverage = <
  T extends IReturnExerciseChartData,
>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d.duration.max}`,
      `Avg: ${d.duration.avg}`,
      `Min: ${d.duration.min}`,
    ];
  });

export const getTooltipHeartRateMessagesFromAverage = <
  T extends IReturnHeartRateChartData,
>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [`Max: ${d.bpm.max}`, `Avg: ${d.bpm.avg}`, `Min: ${d.bpm.min}`];
  });

export const getTooltipWaistMessagesFromAverage = <
  T extends IReturnWaistChartData,
>(
  filteredData: T[],
  type: "currentWaist" | "waistGoal",
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d[type].max}`,
      `Avg: ${d[type].avg}`,
      `Min: ${d[type].min}`,
    ];
  });

export const getTooltipStepsMessagesFromAverage = <
  T extends IReturnStepsChartData,
>(
  filteredData: T[],
  type: "stepCount" | "calories",
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d[type].max}`,
      `Avg: ${d[type].avg}`,
      `Min: ${d[type].min}`,
    ];
  });

export const getTooltipBloodOxygenMessagesFromAverage = <
  T extends IReturnBloodOxygenChartData,
>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d.value.max}`,
      `Avg: ${d.value.avg}`,
      `Min: ${d.value.min}`,
    ];
  });

export const getTooltipHRVMessagesFromAverage = <T extends IReturnHRVChartData>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d.beats.max}`,
      `Avg: ${d.beats.avg}`,
      `Min: ${d.beats.min}`,
    ];
  });

export const getTooltipStressMessagesFromAverage = <
  T extends IReturnStressChartData,
>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d.stressLevelSkinConductance.max}`,
      `Avg: ${d.stressLevelSkinConductance.avg}`,
      `Min: ${d.stressLevelSkinConductance.min}`,
    ];
  });

export const getTooltipBmiMessagesFromAverage = <T extends IReturnBmiChartData>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [
      `Max: ${Number(d.bmi.max).toFixed(2)}`,
      `Avg: ${Number(d.bmi.avg).toFixed(2)}`,
      `Min: ${Number(d.bmi.min).toFixed(2)}`,
    ];
  });

export const getTooltipHeightMessagesFromAverage = <
  T extends IReturnHeightChartData,
>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [
      `Max: ${d.value.max}`,
      `Avg: ${d.value.avg}`,
      `Min: ${d.value.min}`,
    ];
  });

export const getTooltipRespirationMessagesFromAverage = <
  T extends IReturnRespirationChartData,
>(
  filteredData: T[],
) =>
  filteredData.map((d) => {
    return [`Max: ${d.rate.max}`, `Avg: ${d.rate.avg}`, `Min: ${d.rate.min}`];
  });

export const msToHoursMinutes = (ms: number) => {
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return { hours, minutes };
};

export const msToHours = (ms: number) => ms / 3_600_000;

export const prepareHeightFtIn = (
  feet: number | string,
  inches: number | string,
) => {
  const IN =
    Number(inches) < 10 && Number(inches) > 0
      ? `0${inches}`
      : inches.toString();

  return Number(`${feet}.${IN}`);
};

export const normalizeInches = (inchesStr: string): number => {
  // From BE can be received numbers like
  // 5.1 which means 5 FT 10 IN
  // 5.01 which means 5 FT 1 IN
  // and wrong values which can not be parsed right (were added to DB before right implementation). Example: 4.95
  // such number inches will be truncated 4.95 will be 4 FT 9 IN

  // Handle single digit with leading 1 (e.g., "1" -> "10")
  if (inchesStr.length === 1 && inchesStr === "1") {
    return 10;
  }

  // Handle two digits starting with 0 (e.g., "01" -> "1")
  if (inchesStr.length === 2 && inchesStr.startsWith("0")) {
    return Number(inchesStr.slice(1));
  }

  // Handle invalid inches (>11), take first digit only
  const parsed = Number(inchesStr);
  if (inchesStr.length === 2 && parsed > 11) {
    return Number(inchesStr[0]);
  }

  return parsed;
};

export const parseFtIn = (value: number) => {
  const [feetStr, inchesStr = "0"] = value.toString().split(".");
  const feet = Number(feetStr);
  const inches = normalizeInches(inchesStr);

  return { feet, inches };
};
